import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, ChevronRight, Mail, PenLine, Phone, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  isSameEmail,
  isValidEmailOtp,
  loadAuthenticatedBookingProfile,
  persistLocalBookingProfile,
  syncPublicUserProfile,
} from '../lib/bookingService';
import { formatPhilippineMobileDisplay, normalizePhilippineMobile, validatePhilippineMobile } from '../lib/phoneNumber';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EditProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sendingCode, setSendingCode] = useState(false);
  const [confirmingCode, setConfirmingCode] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEmailEditorOpen, setIsEmailEditorOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userId, setUserId] = useState('');
  const [isPhoneEditorOpen, setIsPhoneEditorOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const profile = await loadAuthenticatedBookingProfile();

        if (!profile.email && !localStorage.getItem('userAuthenticated')) {
          navigate('/login', { replace: true });
          return;
        }

        setUserId(user?.id || '');
        setUserEmail(profile.email);
        setUserName(profile.name);
        setUserPhone(profile.phone || '');
        setPhoneDraft(profile.phone || '');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleBack = () => {
    navigate('/profile');
  };

  const handleNameClick = () => {
    navigate('/profile/change-name');
  };

  const handlePhoneRowClick = () => {
    setIsPhoneEditorOpen((prev) => !prev);
    setPhoneDraft(userPhone);
    setMessage('');
    setError('');
  };

  const savePhoneNumber = async () => {
    const phoneError = validatePhilippineMobile(phoneDraft, { required: true });
    if (phoneError) {
      setError(phoneError);
      setMessage('');
      return;
    }

    const normalizedPhone = normalizePhilippineMobile(phoneDraft);
    setSavingPhone(true);
    setError('');
    setMessage('');

    try {
      await syncPublicUserProfile({
        id: userId,
        email: userEmail,
        full_name: userName,
        phone: normalizedPhone,
      });
      setUserPhone(normalizedPhone);
      setPhoneDraft(normalizedPhone);
      setIsPhoneEditorOpen(false);
      setMessage('Mobile number saved. New bookings will use this contact number.');
    } catch (saveErr) {
      setError(saveErr.message || 'Unable to save mobile number.');
    } finally {
      setSavingPhone(false);
    }
  };

  const resetEmailEditor = () => {
    setCodeSent(false);
    setVerificationCode('');
    setMessage('');
    setError('');
  };

  const handleEmailRowClick = () => {
    setIsEmailEditorOpen((prev) => {
      const next = !prev;
      if (!next) {
        setNewEmail('');
        resetEmailEditor();
      } else {
        resetEmailEditor();
      }
      return next;
    });
  };

  const sendEmailChangeCode = async () => {
    const targetEmail = newEmail.trim();
    if (!EMAIL_PATTERN.test(targetEmail)) {
      setError('Please enter a valid email address.');
      setMessage('');
      return;
    }

    if (isSameEmail(targetEmail, userEmail)) {
      setError('Enter a different email address than the one already on this account.');
      setMessage('');
      return;
    }

    setSendingCode(true);
    setError('');
    setMessage('');

    try {
      const { error: updateError } = await supabase.auth.updateUser({ email: targetEmail });
      if (updateError) throw updateError;

      localStorage.setItem('pendingUserEmail', targetEmail);
      setCodeSent(true);
      setMessage(`A 6-digit verification code was sent to ${targetEmail}.`);
    } catch (updateErr) {
      setError(updateErr.message || 'Unable to send verification code.');
    } finally {
      setSendingCode(false);
    }
  };

  const confirmEmailChange = async () => {
    const targetEmail = newEmail.trim();
    const token = verificationCode.trim();

    if (!isValidEmailOtp(token)) {
      setError('Enter the 6-digit code sent to your new email.');
      setMessage('');
      return;
    }

    setConfirmingCode(true);
    setError('');
    setMessage('');

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: targetEmail,
        token,
        type: 'email_change',
      });
      if (verifyError) throw verifyError;

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const confirmedEmail = user?.email || targetEmail;
      const confirmedName =
        user?.user_metadata?.full_name ||
        user?.user_metadata?.name ||
        userName;

      await syncPublicUserProfile({
        id: user?.id,
        email: confirmedEmail,
        full_name: confirmedName,
      });

      persistLocalBookingProfile({ email: confirmedEmail, name: confirmedName });
      localStorage.removeItem('pendingUserEmail');
      setUserEmail(confirmedEmail);
      setUserName(confirmedName);
      setNewEmail('');
      setVerificationCode('');
      setCodeSent(false);
      setIsEmailEditorOpen(false);
      setMessage('Email updated successfully.');
    } catch (verifyErr) {
      setError(verifyErr.message || 'Unable to verify the email code.');
    } finally {
      setConfirmingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-8rem)] overflow-hidden bg-[#141414] text-white grid place-items-center px-6 py-6">
        <p className="text-sm text-white/60">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh-8rem)] overflow-y-auto bg-[#141414] text-white px-5 py-4 md:py-6">
      <div className="mx-auto flex h-full max-w-sm flex-col items-center justify-start pt-3 md:pt-4">
        <div className="w-full">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/80 transition hover:text-white"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="mt-2 text-center">
            <h1 className="text-[28px] font-semibold tracking-tight text-white">Edit Profile</h1>
          </div>

          <div className="mt-4 flex flex-col items-center text-center">
            <div className="grid h-24 w-24 place-items-center rounded-full border-2 border-white bg-transparent">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-white text-[#141414]">
                <UserRound size={34} strokeWidth={1.75} />
              </div>
            </div>

            <p className="mt-2 text-[13px] uppercase tracking-[0.22em] text-white/40">Current profile</p>
            <h2 className="mt-1 text-[26px] font-semibold leading-tight tracking-tight text-white">
              {userName || 'Your Profile'}
            </h2>
            <p className="mt-2 text-[13px] text-white/55">{userEmail || 'No email available'}</p>
            <p className="mt-1 text-[13px] text-white/55">
              {userPhone ? formatPhilippineMobileDisplay(userPhone) : 'No mobile number saved'}
            </p>
          </div>

          <div className="mt-3 space-y-3">
            <button
              type="button"
              onClick={handleNameClick}
              className="flex w-full items-center justify-between rounded-[18px] bg-[#5b5b5b] px-4 py-4 text-left text-white/90 shadow-[0_10px_20px_rgba(0,0,0,0.24)] transition active:scale-[0.99]"
            >
              <span className="flex items-center gap-3">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/90">
                  <PenLine size={16} />
                </span>
                <span className="text-[14px] font-medium">Name</span>
              </span>
              <span className="flex items-center gap-2 text-[13px] text-white/80">
                <span className="max-w-[12rem] truncate">{userName || 'Add name'}</span>
                <ChevronRight size={16} />
              </span>
            </button>

            <button
              type="button"
              onClick={handlePhoneRowClick}
              className="flex w-full items-center justify-between rounded-[18px] bg-[#5b5b5b] px-4 py-4 text-left text-white/90 shadow-[0_10px_20px_rgba(0,0,0,0.24)] transition active:scale-[0.99]"
            >
              <span className="flex items-center gap-3">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/90">
                  <Phone size={16} />
                </span>
                <span className="text-[14px] font-medium">Mobile Number</span>
              </span>
              <span className="flex items-center gap-2 text-[13px] text-white/80">
                <span className="max-w-[12rem] truncate">{userPhone ? formatPhilippineMobileDisplay(userPhone) : 'Add number'}</span>
                <ChevronRight size={16} />
              </span>
            </button>

            {isPhoneEditorOpen && (
              <div className="rounded-[18px] border border-white/10 bg-[#232323] p-4">
                <p className="text-[11px] leading-relaxed text-white/45">
                  Add a mobile number to use as your default contact number for bookings.
                </p>
                <label className="mb-2 mt-3 block text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
                  Mobile number
                </label>
                <input
                  type="tel"
                  value={phoneDraft}
                  onChange={(e) => setPhoneDraft(e.target.value)}
                  placeholder="09XXXXXXXXX or +639XXXXXXXXX"
                  className="w-full rounded-[14px] border border-white/10 bg-[#1a1a1a] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/15"
                />
                <button
                  type="button"
                  onClick={savePhoneNumber}
                  disabled={savingPhone}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#1e6fff] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#125ee6] disabled:opacity-60"
                >
                  <Check size={16} />
                  {savingPhone ? 'Saving...' : 'Save mobile number'}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={handleEmailRowClick}
              className="flex w-full items-center justify-between rounded-[18px] bg-[#5b5b5b] px-4 py-4 text-left text-white/90 shadow-[0_10px_20px_rgba(0,0,0,0.24)] transition active:scale-[0.99]"
            >
              <span className="flex items-center gap-3">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/90">
                  <Mail size={16} />
                </span>
                <span className="text-[14px] font-medium">Email</span>
              </span>
              <span className="flex items-center gap-2 text-[13px] text-white/80">
                <span className="max-w-[12rem] truncate">{userEmail || 'Add email'}</span>
                <ChevronRight size={16} />
              </span>
            </button>

            {isEmailEditorOpen && (
              <div className="rounded-[18px] border border-white/10 bg-[#232323] p-4">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
                  New email address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={userEmail || 'Enter new email'}
                  disabled={codeSent}
                  className="w-full rounded-[14px] border border-white/10 bg-[#1a1a1a] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/15 disabled:opacity-60"
                />

                {!codeSent ? (
                  <button
                    type="button"
                    onClick={sendEmailChangeCode}
                    disabled={sendingCode}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#1e6fff] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#125ee6] disabled:opacity-60"
                  >
                    <Check size={16} />
                    {sendingCode ? 'Sending...' : 'Send verification code'}
                  </button>
                ) : (
                  <>
                    <label className="mb-2 mt-4 block text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
                      6-digit code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      className="w-full rounded-[14px] border border-white/10 bg-[#1a1a1a] px-4 py-3 text-center text-lg tracking-[0.4em] text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/15"
                    />

                    <button
                      type="button"
                      onClick={confirmEmailChange}
                      disabled={confirmingCode}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#1e6fff] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#125ee6] disabled:opacity-60"
                    >
                      <Check size={16} />
                      {confirmingCode ? 'Confirming...' : 'Confirm email'}
                    </button>

                    <button
                      type="button"
                      onClick={sendEmailChangeCode}
                      disabled={sendingCode}
                      className="mt-2 inline-flex w-full items-center justify-center rounded-[14px] border border-white/15 px-4 py-3 text-sm font-semibold text-white/80 transition hover:bg-white/5 disabled:opacity-60"
                    >
                      {sendingCode ? 'Sending...' : 'Resend code'}
                    </button>
                  </>
                )}

                <p className="mt-3 text-[11px] leading-relaxed text-white/45">
                  A 6-digit code will be sent to the new email. The current email stays active until that code is confirmed.
                </p>
              </div>
            )}

            {message ? <p className="text-xs font-medium text-emerald-300">{message}</p> : null}
            {error ? <p className="text-xs font-medium text-rose-300">{error}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
